import {
  CardAction,
  CardFacts,
  CardTimeline,
  type CardItem,
  type CardTimelineData,
  type OnCardAction,
  OrderItemRows,
} from '../order-card/CardParts';
import { useCardPhrases } from '../order-card/CardLanguage';
import {
  asArray,
  formatCardDate,
  formatCardItemCount,
  formatCardPrice,
  formatDateWindow,
  formatOrderHeading,
  formatPostalCode,
} from '../order-card/order-card-format';

export type OrderInfoCardData = {
  kind?: 'order_detail' | 'order_items';
  orderNumber: string;
  date?: string;
  timeline?: CardTimelineData;
  delivery?: {
    method?: string;
    windowStart?: string;
    windowEnd?: string;
    address?: { street?: string; postalCode?: string; city?: string };
    trackingNumber?: string;
    trackingUrl?: string | null;
  };
  totalPrice?: number;
  currency?: string;
  items?: CardItem[];
};

// Samma kort som live-widgetens renderOrderInfoCard (chat_embed.js): detaljen
// visar tidslinje + leverans, varulistan hoppar dem — frågan gäller innehållet.
export function OrderInfoCard({
  info,
  onAction,
}: {
  info: OrderInfoCardData;
  onAction?: OnCardAction;
}) {
  const phrases = useCardPhrases();
  const isDetail = info.kind !== 'order_items';
  const items = asArray<CardItem | null>(info.items).filter((item): item is CardItem =>
    Boolean(item),
  );
  const itemCountLabel = formatCardItemCount(
    items.reduce((sum, item) => sum + (Number(item?.quantity) || 1), 0),
    phrases,
  );
  const dateLabel = formatCardDate(info.date) || String(info.date ?? '');
  const aside = isDetail ? dateLabel : itemCountLabel || dateLabel;

  const delivery = isDetail ? info.delivery : undefined;
  const factRows: Array<[string, string]> = [];
  if (delivery) {
    const method = String(delivery.method ?? '').trim();
    if (method) factRows.push([phrases.factMethod, method]);
    const window = formatDateWindow(delivery.windowStart, delivery.windowEnd, phrases.locale);
    if (window) factRows.push([phrases.factWindow, window]);
    const address = delivery.address ?? {};
    const postalCity = [formatPostalCode(address.postalCode), String(address.city ?? '').trim()]
      .filter(Boolean)
      .join(' ');
    const addressLine = [String(address.street ?? '').trim(), postalCity]
      .filter(Boolean)
      .join(', ');
    if (addressLine) factRows.push([phrases.factAddress, addressLine]);
  }

  const total = Number(info.totalPrice);
  const sum =
    Number.isFinite(total) && total > 0
      ? `${phrases.total} ${formatCardPrice(total, info.currency, phrases.locale)}`
      : '';
  const hasItems = items.some((item) => item?.name);

  return (
    <article className="ai-chat__card">
      <div className="ai-chat__card-head">
        <div className="ai-chat__card-title">{formatOrderHeading(info.orderNumber, phrases)}</div>
        {aside ? <div className="ai-chat__card-aside">{aside}</div> : null}
      </div>
      {isDetail ? <CardTimeline timeline={info.timeline} /> : null}
      <CardFacts rows={factRows} />
      {hasItems ? (
        <div className="ai-chat__card-items">
          {/* Varulistan bär redan antalet i huvudets aside — etiketten blev en dubblett där. */}
          {isDetail && itemCountLabel ? (
            <div className="ai-chat__card-items-label">{itemCountLabel}</div>
          ) : null}
          <OrderItemRows items={items} />
        </div>
      ) : null}
      <div className="ai-chat__card-foot">
        <div className="ai-chat__card-sum">{sum}</div>
        {isDetail ? (
          <CardAction
            label={phrases.trackParcel}
            url={delivery?.trackingUrl ?? ''}
            quietLabel={phrases.noUpdate}
          />
        ) : (
          <CardAction
            label={phrases.seeWholeOrder}
            value="Visa orderinfo"
            actionId="order.info"
            onAction={onAction}
          />
        )}
      </div>
    </article>
  );
}
